import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { logger } from '@/lib/logger'
import { useAdmin } from './use-admin'
import { formatPhone } from '@/lib/utils'
import type { FormEvent } from 'react'

export interface EditarIgrejaForm {
  nome: string
  endereco: string
  cidade: string
  estado: string
  cep: string
  telefone: string
  email: string
  pastor: string
  descricao: string
  dominio: string
  ativa: boolean | null
  cor_primaria: string
  cor_secundaria: string
}

const ESTADOS_BRASILEIROS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
]

export function useEditarIgreja() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { igrejas, updateIgreja, isUpdatingIgreja } = useAdmin()
  
  const [formState, setFormState] = useState<EditarIgrejaForm>({
    nome: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    telefone: '',
    email: '',
    pastor: '',
    descricao: '',
    dominio: '',
    ativa: true,
    cor_primaria: '#3b82f6',
    cor_secundaria: '#64748b'
  })

  const [errors, setErrors] = useState<Partial<EditarIgrejaForm>>({})
  const [isLoading, setIsLoading] = useState(true)

  // Carregar dados da igreja quando o componente montar
  useEffect(() => {
    if (id && igrejas.length > 0) {
      const igreja = igrejas.find(i => i.id === id)
      if (igreja) {
        setFormState({
          nome: igreja.nome || '',
          endereco: igreja.endereco || '',
          cidade: igreja.cidade || '',
          estado: igreja.estado || '',
          cep: igreja.cep || '',
          telefone: igreja.telefone || '',
          email: igreja.email || '',
          pastor: igreja.pastor || '',
          descricao: igreja.descricao || '',
          dominio: igreja.dominio || '',
          ativa: igreja.ativa ?? false,
          cor_primaria: igreja.cor_primaria || '#3b82f6',
          cor_secundaria: igreja.cor_secundaria || '#64748b'
        })
        setIsLoading(false)
      } else {
        toast.error('Igreja não encontrada')
        navigate('/admin-app')
      }
    }
  }, [id, igrejas, navigate])

  const handleInputChange = (field: keyof EditarIgrejaForm, value: string | boolean) => {
    let processedValue = value
    
    // Apply phone mask if field is telefone
    if (field === 'telefone' && typeof value === 'string') {
      processedValue = formatPhone(value)
    }
    
    setFormState(prev => ({
      ...prev,
      [field]: processedValue
    }))
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<EditarIgrejaForm> = {}

    if (!formState.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório'
    }

    if (!formState.endereco.trim()) {
      newErrors.endereco = 'Endereço é obrigatório'
    }

    if (!formState.cidade.trim()) {
      newErrors.cidade = 'Cidade é obrigatória'
    }

    if (!formState.estado) {
      newErrors.estado = 'Estado é obrigatório'
    }

    if (!formState.cep.trim()) {
      newErrors.cep = 'CEP é obrigatório'
    } else if (!/^\d{5}-?\d{3}$/.test(formState.cep)) {
      newErrors.cep = 'CEP deve ter o formato 00000-000'
    }

    if (formState.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = 'Email deve ter um formato válido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || !id) {
      return
    }

    try {
      await updateIgreja({
        id,
        ...formState
      })

      logger.info('Igreja atualizada', { context: 'EDITAR_IGREJA' })
      toast.success('Igreja atualizada com sucesso!')
      navigate('/admin-app')
    } catch (error) {
      logger.error('Erro ao atualizar igreja', { context: 'EDITAR_IGREJA' })
      console.error('Detalhes do erro:', error)
      toast.error('Erro ao atualizar igreja. Tente novamente.')
    }
  }

  const handleCancel = () => {
    navigate('/admin-app')
  }

  return {
    formState,
    errors,
    isLoading,
    isSubmitting: isUpdatingIgreja,
    estadosBrasileiros: ESTADOS_BRASILEIROS,
    handleInputChange,
    handleSubmit,
    handleCancel
  }
}